import { useEffect, useState } from 'react'
import { Link2, RefreshCw, ShieldCheck, Unplug } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Input } from '@/components/ui/Input'
import { toast } from '@/providers/NotificationProvider'
import { getConsoleUrl } from '@/config/runtime'
import {
  clearConsoleBinding,
  getConsoleBindingState,
  pollConsolePairing,
  startConsolePairing,
  type ConsoleBindingSummary,
  type ConsolePairingStart,
} from '@/services/consoleBinding'

function getInitialConsoleUrl() {
  return getConsoleUrl()
}

export default function DesktopConsoleBindingCard() {
  const { t, i18n } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [supported, setSupported] = useState(true)
  const [binding, setBinding] = useState<ConsoleBindingSummary | null>(null)
  const [pairingOpen, setPairingOpen] = useState(false)
  const [consoleUrl, setConsoleUrl] = useState(getInitialConsoleUrl)
  const [pairing, setPairing] = useState<ConsolePairingStart | null>(null)
  const [pairingError, setPairingError] = useState<string | null>(null)
  const [startingPairing, setStartingPairing] = useState(false)
  const [detaching, setDetaching] = useState(false)
  const [detachConfirmOpen, setDetachConfirmOpen] = useState(false)

  useEffect(() => {
    let active = true

    void getConsoleBindingState()
      .then((state) => {
        if (!active) {
          return
        }

        setSupported(state.supported)
        if (!state.binding) {
          return
        }

        setBinding(state.binding)
        setConsoleUrl(state.binding.consoleUrl)
      })
      .finally(() => {
        if (active) {
          setLoading(false)
        }
      })

    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    if (!pairing) {
      return
    }

    let active = true
    let pollInFlight = false
    const pollIntervalMs = Math.max(pairing.interval, 3) * 1000
    const expiryMs = Math.max(pairing.expiresIn, 15) * 1000

    const pollOnce = async () => {
      if (!active || pollInFlight) {
        return
      }

      pollInFlight = true
      try {
        const result = await pollConsolePairing({
          consoleUrl: pairing.consoleUrl,
          deviceCode: pairing.deviceCode,
        })
        if (!active || result.status !== 'paired') {
          return
        }

        setBinding(result.binding)
        setConsoleUrl(result.binding.consoleUrl)
        setPairing(null)
        setPairingOpen(false)
        setPairingError(null)
        toast.success(
          t('sidebar.consolePaired', {
            instance: result.binding.instanceName || result.binding.instanceId,
          }),
        )
        window.setTimeout(() => {
          window.location.reload()
        }, 250)
      } catch (error) {
        if (!active) {
          return
        }

        const message =
          error instanceof Error ? error.message : t('sidebar.consolePairingFailed')
        setPairing(null)
        setPairingError(message)
        toast.error(message)
      } finally {
        pollInFlight = false
      }
    }

    const intervalId = window.setInterval(() => {
      void pollOnce()
    }, pollIntervalMs)
    const expiryId = window.setTimeout(() => {
      if (!active) {
        return
      }

      setPairing(null)
      setPairingError(t('sidebar.consolePairingExpired'))
    }, expiryMs)

    void pollOnce()

    return () => {
      active = false
      window.clearInterval(intervalId)
      window.clearTimeout(expiryId)
    }
  }, [pairing, t])

  const openPairing = () => {
    setPairingOpen(true)
    setPairingError(null)
    setPairing(null)
    setConsoleUrl(binding?.consoleUrl || getInitialConsoleUrl())
  }

  const closePairing = () => {
    setPairingOpen(false)
    setPairing(null)
    setPairingError(null)
  }

  const handleStartPairing = async () => {
    setStartingPairing(true)
    setPairingError(null)

    try {
      const nextPairing = await startConsolePairing({ consoleUrl })
      setPairing(nextPairing)
      setConsoleUrl(nextPairing.consoleUrl)
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t('sidebar.consolePairingFailed')
      setPairingError(message)
      toast.error(message)
    } finally {
      setStartingPairing(false)
    }
  }

  const handleDetach = async () => {
    setDetaching(true)
    try {
      await clearConsoleBinding()
      setBinding(null)
      setPairing(null)
      setPairingOpen(false)
      setPairingError(null)
      toast.success(t('sidebar.consoleDetached'))
      window.setTimeout(() => {
        window.location.reload()
      }, 250)
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t('sidebar.consolePairingFailed')
      toast.error(message)
    } finally {
      setDetaching(false)
    }
  }

  const lastSync = binding
    ? new Date(binding.updatedAt * 1000).toLocaleString(i18n.language)
    : null

  if (!loading && !supported && !binding) {
    return null
  }

  const valueClassName = "mt-1 break-all text-sm leading-relaxed"

  return (
    <div className="min-w-0 overflow-hidden rounded-xl border border-border p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Link2 className="h-4 w-4 shrink-0" />
            {t('sidebar.consoleBinding')}
          </div>
          <p className="break-words text-sm leading-relaxed text-muted-foreground">
            {t('sidebar.consoleBindingDescription')}
          </p>
        </div>
        <span
          className={cn(
            'shrink-0 rounded-full px-2 py-1 text-[11px] font-medium',
            binding
              ? 'bg-success-soft text-success'
              : 'bg-muted text-muted-foreground',
          )}
        >
          {binding ? t('sidebar.consoleBound') : t('sidebar.consoleUnbound')}
        </span>
      </div>

      {binding && !pairingOpen && (
        <div className="mt-3 min-w-0 space-y-3 overflow-hidden rounded-lg bg-muted/30 p-3">
          <div className="grid min-w-0 gap-3">
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-foreground">
                {t('sidebar.consoleUrl')}
              </div>
              <div className={valueClassName}>{binding.consoleUrl}</div>
            </div>
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-foreground">
                {t('sidebar.consoleInstance')}
              </div>
              <div className={valueClassName}>
                {binding.instanceName || binding.instanceId}
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-foreground">
                {t('sidebar.consoleOrganization')}
              </div>
              <div className={valueClassName}>
                {binding.organizationName || binding.fixedOrganizationId}
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-foreground">
                {t('sidebar.consoleUser')}
              </div>
              <div className={valueClassName}>
                {binding.userDisplayName || binding.userEmail || t('common.unknown')}
              </div>
            </div>
          </div>
          {lastSync && (
            <div className="break-words text-xs leading-relaxed text-muted-foreground">
              {t('sidebar.consoleLastSync', { value: lastSync })}
            </div>
          )}
        </div>
      )}

      {pairingOpen ? (
        <div className="mt-3 space-y-3 rounded-lg border border-border bg-muted/20 p-3">
          {!pairing ? (
            <>
              <p className="text-sm text-muted-foreground">
                {t('sidebar.consolePairingIntro')}
              </p>
              <Input
                value={consoleUrl}
                onChange={(event) => setConsoleUrl(event.target.value)}
                placeholder={t('sidebar.consolePairingUrlPlaceholder')}
                disabled={startingPairing}
              />
              {pairingError && (
                <div className="text-sm text-destructive">{pairingError}</div>
              )}
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closePairing}
                  disabled={startingPairing}
                >
                  {t('sidebar.consolePairingCancel')}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => void handleStartPairing()}
                  disabled={startingPairing}
                >
                  {startingPairing ? (
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <ShieldCheck className="mr-2 h-4 w-4" />
                  )}
                  {t('sidebar.consolePairingStart')}
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                {t('sidebar.consolePairingPending')}
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <div className="text-[13px] font-semibold text-foreground">
                    {t('sidebar.consoleVerificationUrl')}
                  </div>
                  <Input value={pairing.verificationUri} readOnly />
                </div>
                <div className="space-y-1">
                  <div className="text-[13px] font-semibold text-foreground">
                    {t('sidebar.consoleUserCode')}
                  </div>
                  <Input value={pairing.userCode} readOnly className="font-mono uppercase" />
                </div>
              </div>
              {pairingError && (
                <div className="text-sm text-destructive">{pairingError}</div>
              )}
              <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                <span>{t('sidebar.consoleCheckingStatus')}</span>
                <span>{t('sidebar.consolePairingExpires', { count: pairing.expiresIn })}</span>
              </div>
              <div className="flex justify-end">
                <Button type="button" variant="outline" onClick={closePairing}>
                  {t('sidebar.consolePairingCancel')}
                </Button>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          {pairingError && <div className="text-sm text-destructive">{pairingError}</div>}
          <div className="grid gap-2">
            <Button
              type="button"
              variant="secondary"
              className="w-full"
              onClick={openPairing}
              disabled={loading || detaching}
            >
              <ShieldCheck className="mr-2 h-4 w-4" />
              {binding ? t('sidebar.consoleReconnect') : t('sidebar.consolePair')}
            </Button>
            {binding && (
              <Button
                type="button"
                variant="outline"
                className="w-full border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={() => setDetachConfirmOpen(true)}
                disabled={detaching}
              >
                <Unplug className="mr-2 h-4 w-4" />
                {t('sidebar.consoleDetach')}
              </Button>
            )}
          </div>
          <div className="break-words text-xs leading-relaxed text-muted-foreground">
            {loading ? t('common.loading') : t('sidebar.consoleReloadHint')}
          </div>
        </div>
      )}
      <ConfirmDialog
        open={detachConfirmOpen}
        onOpenChange={setDetachConfirmOpen}
        title={t('sidebar.consoleDetachConfirm')}
        confirmText={t('sidebar.consoleDetach')}
        cancelText={t('common.cancel')}
        variant="destructive"
        disabled={detaching}
        onConfirm={() => {
          setDetachConfirmOpen(false)
          void handleDetach()
        }}
      />
    </div>
  )
}
